import { i as validateConfig, n as mapFromKeyValues, r as mapToKeyValues, t as Config } from "./chunk-config.mjs";
import { n as FILE_DYNAMICS, r as FILE_ENVS, t as FILE_DEFAULTS } from "./chunk-types.mjs";
import { dirname } from "@dxos/node-std/path";
import * as Yaml from "yaml";
import { invariant } from "@dxos/invariant";
import { isNode } from "@dxos/util";
import * as defs from "@dxos/protocols/proto/dxos/config";
import * as FileSystem from "@effect/platform/FileSystem";
import * as Context from "effect/Context";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import * as Option from "effect/Option";
import { DX_CONFIG, DX_DATA, getProfileConfigPath, getProfilePath } from "@dxos/client-protocol";
import * as Match from "effect/Match";
export * from "#loaders";
export * from "#savers";
export * from "#plugin";
//#region src/config-service.ts
var __dxlog_file = "/__w/dxos/dxos/packages/sdk/config/src/config-service.ts";
var memoryConfig = new Config({ runtime: { client: { edgeFeatures: {
	subductionReplicator: true,
	feedReplicator: true,
	signaling: true,
	agents: true
} } } });
var defaultConfig = new Config({ runtime: {
	client: {
		edgeFeatures: {
			subductionReplicator: true,
			feedReplicator: true,
			signaling: true,
			agents: true
		},
		storage: { persistent: true }
	},
	services: {
		edge: { url: "wss://edge-production.dxos.workers.dev/" },
		iceProviders: [{ urls: "https://edge-production.dxos.workers.dev/ice" }],
		ai: { server: "https://ai-service.dxos.workers.dev" },
		ipfs: {
			server: "https://api.ipfs.dxos.network/api/v0",
			gateway: "https://gateway.ipfs.dxos.network/ipfs"
		}
	}
} });
var ConfigService = class ConfigService extends Context.Tag("ConfigService")() {
	static layerMemory = Layer.effect(ConfigService, Effect.succeed(memoryConfig));
	static fromConfig = (config) => Layer.succeed(ConfigService, config);
	static load = (args) => {
		const defaultConfigPath = getProfileConfigPath(DX_CONFIG, args.profile);
		return Effect.gen(function* () {
			const fs = yield* FileSystem.FileSystem;
			const configPath = Option.getOrElse(args.config, () => defaultConfigPath);
			const configContent = yield* fs.readFileString(configPath);
			const configValues = Yaml.parse(configContent);
			return ConfigService.of(new Config(configValues, profileBuiltinDefaults(args.profile).values));
		}).pipe(Effect.catchTag("SystemError", () => Effect.gen(function* () {
			const configValues = defaultConfig.values;
			const fs = yield* FileSystem.FileSystem;
			const pathToCreate = Option.getOrElse(args.config, () => defaultConfigPath);
			yield* fs.makeDirectory(dirname(pathToCreate), { recursive: true });
			yield* fs.writeFileString(pathToCreate, Yaml.stringify(configValues));
			return ConfigService.of(new Config(configValues));
		})));
	};
};
/**
* Default config for a profile.
* Always merged with the default config.
*/
var profileBuiltinDefaults = (profile) => {
	invariant(!profile.endsWith(".yml"), void 0, {
		"~LogMeta": "~LogMeta",
		F: __dxlog_file,
		L: 98,
		S: void 0,
		A: ["!profile.endsWith('.yml')", ""]
	});
	return new Config({ runtime: { client: {
		edgeFeatures: {
			subductionReplicator: true,
			feedReplicator: true,
			signaling: true,
			agents: true
		},
		storage: {
			persistent: true,
			dataRoot: getProfilePath(DX_DATA, profile)
		}
	} } });
};
//#endregion
//#region src/edge-services.ts
/**
* Canonical names for EDGE (Cloudflare Worker) services.
* Used as keys into `runtime.services.edgeServices` and {@link EDGE_SERVICE_DEFAULTS}.
*/
var EdgeServiceName = Object.freeze({
	Calls: "calls",
	Image: "image",
	Transcription: "transcription",
	Discord: "discord",
	CorsProxy: "cors-proxy",
	ApiProxy: "api-proxy",
	Introspect: "introspect",
	ChatAgent: "chat-agent"
});
/**
* Canonical dev/test default endpoints for EDGE services.
* Single source of truth for the URLs previously hard-coded across plugins.
* Production values are supplied per-app via `dx.yml` (`runtime.services.edgeServices`).
*/
var EDGE_SERVICE_DEFAULTS = Object.freeze({
	[EdgeServiceName.Calls]: "https://calls-service.dxos.workers.dev",
	[EdgeServiceName.Image]: "https://image-service-main.dxos.workers.dev",
	[EdgeServiceName.Transcription]: "https://calls-service.dxos.workers.dev",
	[EdgeServiceName.Discord]: "https://discord-service.dxos.workers.dev",
	[EdgeServiceName.CorsProxy]: "https://cors-proxy.dxos.workers.dev",
	[EdgeServiceName.ApiProxy]: "https://api-proxy.dxos.workers.dev",
	[EdgeServiceName.Introspect]: "https://introspect-service-labs.dxos.workers.dev/mcp",
	[EdgeServiceName.ChatAgent]: "wss://chat-agent-labs.dxos.workers.dev"
});
/**
* Resolve the endpoint for an EDGE service.
* Prefers the matching `runtime.services.edgeServices` entry, falling back to the canonical
* {@link EDGE_SERVICE_DEFAULTS} entry.
* `name` is expected to be unique; on duplicates the last entry wins so a later override is
* not silently shadowed by an earlier one (proto cannot enforce uniqueness on a repeated field).
*/
var getEdgeServiceEndpoint = (config, name) => config.values.runtime?.services?.edgeServices?.findLast((service) => service.name === name)?.endpoint ?? EDGE_SERVICE_DEFAULTS[name];
//#endregion
//#region src/telemetry.ts
/**
* Resolves the telemetry tag used as the `X-DXOS-Client-Tag` header on edge
* requests AND as the `ctx.tag` attribute / resource on observability traces.
*
* Keeping a single resolver ensures the same tag identifies a session across
* edge and telemetry tiers. Precedence:
*
*   1. `config.runtime.app.env.DX_TELEMETRY_TAG` — the explicit, deterministic
*      path. Populated from `.env`/build-time by the app's config loaders.
*   2. `process.env.DX_TELEMETRY_TAG` — Node-only, for dev/CI convenience.
*
* Returns `undefined` when unset; callers should no-op on undefined rather
* than stamping empty strings.
*/
var resolveTelemetryTag = (config) => {
	return config?.get("runtime.app.env.DX_TELEMETRY_TAG") ?? (isNode() ? process.env.DX_TELEMETRY_TAG : void 0);
};
//#endregion
//#region src/preset.ts
var edgeUrl = (edge) => Match.value(edge).pipe(Match.when("local", () => "http://localhost:8787"), Match.when("dev", () => "https://edge.dxos.workers.dev"), Match.when("main", () => "https://edge-main.dxos.workers.dev"), Match.when("production", () => "https://edge-production.dxos.workers.dev"), Match.exhaustive);
var sandboxUrl = (sandbox) => Match.value(sandbox).pipe(Match.when("local", () => "http://localhost:8792"), Match.when("dev", () => "https://sandbox-service.dxos.workers.dev"), Match.when("main", () => "https://sandbox-service.dxos.workers.dev"), Match.when("production", () => "https://sandbox-service.dxos.workers.dev"), Match.exhaustive);
var configPreset = ({ edge = "main", sandbox } = {}) => new Config({
	version: 1,
	runtime: {
		client: { edgeFeatures: {
			signaling: true,
			subductionReplicator: true,
			feedReplicator: true
		} },
		services: {
			edge: { url: edgeUrl(edge) },
			...sandbox ? { sandbox: { url: sandboxUrl(sandbox) } } : {}
		}
	}
});
//#endregion
export { Config, ConfigService, EDGE_SERVICE_DEFAULTS, EdgeServiceName, FILE_DEFAULTS, FILE_DYNAMICS, FILE_ENVS, configPreset, defaultConfig, defs, getEdgeServiceEndpoint, mapFromKeyValues, mapToKeyValues, memoryConfig, resolveTelemetryTag, validateConfig };

//# sourceMappingURL=index.mjs.map